// PDP gallery controller (snippets/product-gallery.liquid). Single controller
// per [data-product-gallery] root - initialized once (guarded by
// data-gallery-initialized) and exposed on `root.veloGallery` so
// assets/product-form.js can move the gallery to a variant's featured media
// via selectByMediaId/selectBySrc instead of touching gallery DOM itself
// (avoids two competing controllers on the same [data-gallery-image]/
// [data-gallery-thumbnail] elements).
//
// Autoplay: only starts when the product has 2+ image media and
// prefers-reduced-motion is not set. Advances every data-autoplay-speed ms
// (default 1800, Theme Editor setting), loops last->first. Pauses while: the
// tab is hidden, the pointer hovers the gallery, focus is inside gallery
// controls, a drag is in progress, or reduced-motion is enabled - and resumes
// when the condition ends. Any manual selection (thumbnail, prev/next,
// keyboard arrow, swipe/drag, variant-featured-media) resets the timer.
//
// Manual control: Pointer Events power touch/mouse swipe-drag on the main
// image (data-gallery-main, touch-action: pan-y so vertical page scroll is
// never blocked). preventDefault() is only called once horizontal intent is
// confirmed, so a vertical swipe/scroll is left entirely to the browser. A
// real drag suppresses the trailing click so it can't be mistaken for a tap.
//
// Supports Theme Editor shopify:section:load/unload/select so re-rendered
// sections don't leak duplicate timers or listeners.
(function () {
  var DRAG_THRESHOLD = 40;
  var DIRECTION_LOCK_THRESHOLD = 10;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Scrolls the active thumbnail into view within its own horizontal strip
  // ONLY - never uses Element.scrollIntoView(), which walks the full
  // ancestor chain (including the page itself) and can trigger an unwanted
  // vertical page-scroll-to-top on some mobile browsers even with
  // block:'nearest'. This clamps scrollLeft on the immediate scroll
  // container instead, so autoplay/swipe/thumbnail-click can never move the
  // page.
  // Rect-based so it works in RTL too, where scrollLeft runs 0 -> negative and
  // offsetLeft-based clamping would pin the strip at its start.
  function scrollThumbIntoView(thumb) {
    var container = thumb.parentElement;
    if (!container) return;
    var c = container.getBoundingClientRect();
    var t = thumb.getBoundingClientRect();
    if (t.left < c.left) {
      container.scrollLeft += t.left - c.left;
    } else if (t.right > c.right) {
      container.scrollLeft += t.right - c.right;
    }
  }

  // Thumbnail strip is slideable: native swipe on touch, click-and-drag with a
  // mouse on desktop (works the same in RTL - scrollLeft is relative).
  function initThumbStrip(root) {
    var strip = root.querySelector('[data-gallery-thumbnails]');
    if (!strip) return function () {};

    var dragStartX = 0;
    var dragStartScroll = 0;
    var dragPointer = null;
    var dragMoved = false;

    strip.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      dragPointer = e.pointerId;
      dragStartX = e.clientX;
      dragStartScroll = strip.scrollLeft;
      dragMoved = false;
    });
    strip.addEventListener('pointermove', function (e) {
      if (dragPointer === null || e.pointerId !== dragPointer) return;
      var dx = e.clientX - dragStartX;
      if (!dragMoved && Math.abs(dx) > 5) {
        dragMoved = true;
        strip.style.scrollSnapType = 'none';
        strip.style.cursor = 'grabbing';
        if (strip.setPointerCapture) {
          try { strip.setPointerCapture(dragPointer); } catch (err) { /* noop */ }
        }
      }
      if (dragMoved) strip.scrollLeft = dragStartScroll - dx;
    });
    function endStripDrag() {
      dragPointer = null;
      strip.style.scrollSnapType = '';
      strip.style.cursor = 'grab';
    }
    strip.addEventListener('pointerup', endStripDrag);
    strip.addEventListener('pointercancel', endStripDrag);
    // A drag must not also select the thumbnail it started on.
    strip.addEventListener('click', function (e) {
      if (dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        dragMoved = false;
      }
    }, true);

    return function () {};
  }

  function initGallery(root) {
    if (root.dataset.galleryInitialized === 'true') return;

    var main = root.querySelector('[data-gallery-main]');
    var images = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-image]'));
    var thumbnails = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumbnail]'));
    var prevBtn = root.querySelector('[data-gallery-prev]');
    var nextBtn = root.querySelector('[data-gallery-next]');
    if (!main || !images.length) return;

    root.dataset.galleryInitialized = 'true';

    var current = 0;
    var speed = parseInt(root.dataset.autoplaySpeed, 10) || 1800;
    var autoplayAllowed = root.dataset.autoplay !== 'false' && images.length > 1;
    var timer = null;
    var hovering = false;
    var focused = false;
    var dragging = false;

    function goTo(index) {
      current = (index + images.length) % images.length;

      images.forEach(function (img) {
        var active = parseInt(img.dataset.index, 10) === current;
        img.classList.toggle('opacity-0', !active);
        img.classList.toggle('pointer-events-none', !active);
      });

      thumbnails.forEach(function (thumb) {
        var active = parseInt(thumb.dataset.index, 10) === current;
        thumb.classList.toggle('border-primary', active);
        thumb.classList.toggle('border-transparent', !active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) {
          scrollThumbIntoView(thumb);
        }
      });
    }

    function canAutoplay() {
      return autoplayAllowed
        && !prefersReducedMotion()
        && !document.hidden
        && !hovering
        && !focused
        && !dragging;
    }

    function stopTimer() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function startTimer() {
      stopTimer();
      if (!canAutoplay()) return;
      timer = setInterval(function () {
        if (!canAutoplay()) return;
        goTo(current + 1);
      }, speed);
    }

    function resetTimer() {
      stopTimer();
      startTimer();
    }

    function manualGoTo(index) {
      goTo(index);
      resetTimer();
    }

    // Thumbnail click
    thumbnails.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        manualGoTo(parseInt(thumb.dataset.index, 10));
      });
    });

    // Prev/next controls
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        manualGoTo(current - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        manualGoTo(current + 1);
      });
    }

    // Keyboard arrows anywhere focus lands inside the gallery
    // In RTL the sequence runs right-to-left, so "next" is the left arrow / a rightward swipe.
    var forwardStep = document.documentElement.dir === 'rtl' ? -1 : 1;

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        manualGoTo(current + forwardStep);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        manualGoTo(current - forwardStep);
      }
    });

    // Pause while keyboard focus is inside the gallery controls
    root.addEventListener('focusin', function () {
      focused = true;
      resetTimer();
    });
    root.addEventListener('focusout', function () {
      focused = false;
      resetTimer();
    });

    // Pause on desktop hover
    root.addEventListener('mouseenter', function () {
      hovering = true;
      resetTimer();
    });
    root.addEventListener('mouseleave', function () {
      hovering = false;
      resetTimer();
    });

    // Pointer Events swipe/drag (touch, pen, mouse)
    var startX = 0;
    var startY = 0;
    var pointerId = null;
    var directionLocked = null; // 'horizontal' | 'vertical' | null
    var suppressNextClick = false;

    main.addEventListener('pointerdown', function (e) {
      if (images.length < 2) return;
      startX = e.clientX;
      startY = e.clientY;
      pointerId = e.pointerId;
      directionLocked = null;
      dragging = true;
      stopTimer();
    });

    main.addEventListener('pointermove', function (e) {
      if (pointerId === null || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;

      if (!directionLocked) {
        if (Math.abs(dx) > DIRECTION_LOCK_THRESHOLD || Math.abs(dy) > DIRECTION_LOCK_THRESHOLD) {
          directionLocked = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
          if (directionLocked === 'horizontal' && main.setPointerCapture) {
            try { main.setPointerCapture(pointerId); } catch (err) { /* noop */ }
          }
        }
      }

      if (directionLocked === 'horizontal') {
        e.preventDefault();
      }
    });

    function endDrag(e) {
      if (pointerId === null || (e && e.pointerId !== pointerId && e.pointerId !== undefined)) {
        dragging = false;
        return;
      }

      if (directionLocked === 'horizontal' && e) {
        var dx = e.clientX - startX;
        if (Math.abs(dx) >= DRAG_THRESHOLD) {
          suppressNextClick = true;
          manualGoTo(dx < 0 ? current + forwardStep : current - forwardStep);
        } else {
          resetTimer();
        }
      } else {
        resetTimer();
      }

      pointerId = null;
      directionLocked = null;
      dragging = false;
    }

    main.addEventListener('pointerup', endDrag);
    main.addEventListener('pointercancel', endDrag);

    // Suppress the accidental click a real drag would otherwise fire
    main.addEventListener('click', function (e) {
      if (suppressNextClick) {
        e.preventDefault();
        e.stopPropagation();
        suppressNextClick = false;
      }
    }, true);

    // Prevent the browser's native image-drag ghost
    main.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    // Pause while tab is hidden
    function handleVisibilityChange() {
      if (document.hidden) {
        stopTimer();
      } else {
        resetTimer();
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Reduced-motion preference can change at runtime
    var reducedMotionQuery = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotionQuery && reducedMotionQuery.addEventListener) {
      reducedMotionQuery.addEventListener('change', resetTimer);
    }

    // Public API used by assets/product-form.js to move the gallery to a
    // variant's featured media without a second controller touching this DOM.
    root.veloGallery = {
      selectByMediaId: function (mediaId) {
        if (mediaId === null || mediaId === undefined) return;
        var match = images.findIndex(function (img) {
          return img.dataset.mediaId === String(mediaId);
        });
        if (match === -1) return;
        manualGoTo(match);
      },
      selectBySrc: function (src) {
        if (!src) return;
        var match = images.findIndex(function (img) {
          return img.getAttribute('src') === src;
        });
        if (match === -1) return;
        manualGoTo(match);
      },
      resetTimer: resetTimer
    };

    var stopThumbStrip = initThumbStrip(root);

    root._veloGalleryStop = function () {
      stopTimer();
      stopThumbStrip();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (reducedMotionQuery && reducedMotionQuery.removeEventListener) {
        reducedMotionQuery.removeEventListener('change', resetTimer);
      }
    };

    goTo(0);
    startTimer();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-product-gallery]:not([data-gallery-initialized="true"])').forEach(initGallery);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });
  if (document.readyState !== 'loading') {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-product-gallery]').forEach(function (root) {
      if (root._veloGalleryStop) root._veloGalleryStop();
      root.dataset.galleryInitialized = 'false';
    });
  });

  document.addEventListener('shopify:section:select', function (e) {
    e.target.querySelectorAll('[data-product-gallery]').forEach(function (root) {
      if (root.veloGallery) root.veloGallery.resetTimer();
    });
  });
})();
